import "./styles.scss";

import React, { useRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, Slider } from "@nextui-org/react";
import { AiFillPauseCircle, AiFillPlayCircle } from "react-icons/ai";
import { FiChevronsRight, FiChevronsLeft } from "react-icons/fi";
import { FaPlay, FaPause } from "react-icons/fa";
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from "react-icons/io";

import SeekableTime from "./SeekableTime";
import { formatTimecode } from "./timecode";
import { useAppContext } from "../../contexts/AppContext";

const MAudioPlayer = ({ mode = "small", className = "" }) => {
  const {
    popupAudioRef,
    sidebarAudioRef,
    popupAudioUrl,
    popupIsAudioPlaying,
    popupAutoPlay,
    popupAudioTime,
    popupAudioSpeed,
    setPopupAudioSpeed,
    sidebarAudioSpeed,
    setSidebarAudioSpeed,
    sidebarAudioUrl,
    sidebarIsAudioPlaying,
    sidebarAutoPlay,
    sidebarAudioTime,
    setPopupAudioUrl,
    setPopupIsAudioPlaying,
    setPopupAutoPlay,
    setPopupAudioTime,
    setSidebarAudioUrl,
    setSidebarIsAudioPlaying,
    setSidebarAutoPlay,
    setSidebarAudioTime,
    sidebarAudioDuration,
    setSidebarAudioDuration,
    popupAudioDuration,
    setPopupAudioDuration,
    popupCallId,
    sidebarCallId,
    setSidebarCallId,
  } = useAppContext();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const isSidebar = mode === "small";
  const isCallCard = mode === "big";
  const isLiveContentSearch = mode === "live";

  const sidebarData = {
    audioRef: sidebarAudioRef,
    audioUrl: sidebarAudioUrl,
    isAudioPlaying: sidebarIsAudioPlaying,
    autoPlay: sidebarAutoPlay,
    audioTime: sidebarAudioTime,
    setAudioUrl: setSidebarAudioUrl,
    setIsAudioPlaying: setSidebarIsAudioPlaying,
    setAutoPlay: setSidebarAutoPlay,
    setAudioTime: setSidebarAudioTime,
    audioDuration: sidebarAudioDuration,
    setAudioDuration: setSidebarAudioDuration,
    audioSpeed: sidebarAudioSpeed,
    setAudioSpeed: setSidebarAudioSpeed,
  };

  const popupData = {
    audioRef: popupAudioRef,
    audioUrl: popupAudioUrl,
    isAudioPlaying: popupIsAudioPlaying,
    autoPlay: popupAutoPlay,
    audioTime: popupAudioTime,
    setAudioUrl: setPopupAudioUrl,
    setIsAudioPlaying: setPopupIsAudioPlaying,
    setAutoPlay: setPopupAutoPlay,
    setAudioTime: setPopupAudioTime,
    audioDuration: popupAudioDuration,
    setAudioDuration: setPopupAudioDuration,
    audioSpeed: popupAudioSpeed,
    setAudioSpeed: setPopupAudioSpeed,
  };

  const audioData = isSidebar ? sidebarData : popupData;

  const [hasError, setHasError] = useState(false);
  const [speedDiffDirection, setSpeedDiffDirection] = useState("up");

  useEffect(() => {
    setHasError(false);
  }, [audioData.audioUrl]);

  const isAudioLoaded =
    audioData.audioUrl &&
    audioData.audioDuration > 0 &&
    audioData.audioRef &&
    audioData.audioRef.current &&
    !hasError;

  useEffect(() => {
    if (!audioData.autoPlay) return;
    if (!isAudioLoaded) return;

    audioData.audioRef.current.play();
    // audioData.audioRef.current.playbackRate(0);
    audioData.setIsAudioPlaying(true);
    audioData.setAudioSpeed(1);
  }, [audioData.audioRef.current, isAudioLoaded, audioData.audioUrl]);

  const onAudioSpeedClick = () => {
    if (!audioData.audioRef?.current) return;

    if (speedDiffDirection === "up") {
      const newSpeed = Math.min(audioData.audioRef.current.playbackRate + 0.25, 2);
      audioData.audioRef.current.playbackRate = newSpeed;
      audioData.setAudioSpeed(newSpeed);
    }

    if (speedDiffDirection === "down") {
      const newSpeed = Math.max(audioData.audioRef.current.playbackRate - 0.25, 0.25);
      audioData.audioRef.current.playbackRate = newSpeed;
      audioData.setAudioSpeed(newSpeed);
    }

    if (speedDiffDirection === "up" && audioData.audioSpeed === 2) {
      setSpeedDiffDirection("down");
    }
    if (speedDiffDirection === "down" && audioData.audioSpeed === 0.25) {
      setSpeedDiffDirection("up");
    }
  };

  const formattedAudioDuration = formatTimecode(audioData.audioDuration);

  const onBackward = () => {
    if (!audioData.audioRef?.current) return;

    const newSpeed = Math.max(audioData.audioRef.current.playbackRate - 0.25, 0.25);
    audioData.audioRef.current.playbackRate = newSpeed;
    audioData.setAudioSpeed(newSpeed);
  };

  const onForward = () => {
    if (!audioData.audioRef?.current) return;
    const newSpeed = Math.min(audioData.audioRef.current.playbackRate + 0.25, 2);

    audioData.audioRef.current.playbackRate = newSpeed;
    audioData.setAudioSpeed(newSpeed);
  };

  const onPlayPauseClick = () => {
    audioData.setIsAudioPlaying(!audioData.isAudioPlaying);

    if (audioData.audioRef?.current) {
      if (audioData.isAudioPlaying) {
        audioData.audioRef.current.pause();
      } else {
        audioData.audioRef.current.play();
      }
    }

    if (isCallCard || isLiveContentSearch) {
      if (sidebarAudioUrl !== popupAudioUrl) {
        setSidebarAudioUrl(popupAudioUrl);
        setSidebarCallId(popupCallId);
      }

      if (sidebarIsAudioPlaying && sidebarAudioRef && sidebarAudioRef.current) {
        sidebarAudioRef.current.pause();
      }
    }
  };

  // Only the sidebar player offers this: it is the one that keeps playing once
  // you have navigated away from the call.
  const canReturnToCall = isSidebar && !!sidebarCallId;

  const onReturnToCall = () => {
    if (!canReturnToCall) return;

    navigate(`/calls/${sidebarCallId}`);
  };

  // The controls sit inside the clickable card, so they must not bubble.
  const stopClickPropagation = (event) => event.stopPropagation();

  const audioProgress =
    (audioData.audioDuration && (audioData.audioTime * 100) / audioData.audioDuration) || 0;

  const seekToSeconds = (newTime) => {
    if (!audioData.audioRef?.current) return;

    audioData.audioRef.current.currentTime = newTime;
    audioData.setAudioTime(newTime);
  };

  const onSliderChange = (value) => {
    if (!audioData.audioDuration) return;

    seekToSeconds((value / 100) * audioData.audioDuration);
  };

  const onTimeUpdate = () => {
    if (!audioData.audioRef?.current) return;

    audioData.setAudioTime(audioData.audioRef.current.currentTime);
  };

  const onLoadMetadata = () => {
    if (!audioData.audioRef?.current) return;

    console.log({
      data: audioData.audioRef.current,
      duration: audioData.audioRef.current.duration,
    });

    if (audioData.audioRef.current.duration === Infinity) {
      audioData.setAudioDuration(0);
      return;
    }

    audioData.setAudioDuration(audioData.audioRef.current.duration || 0);
  };

  const onAudioError = () => setHasError(true);

  const renderAudio = () => {
    return (
      <audio
        ref={audioData.audioRef}
        src={audioData.audioUrl}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadMetadata}
        onError={onAudioError}
        autoPlay={audioData.autoPlay}
      >
        {/* Include a track element for captions */}
        <track
          kind="captions"
          src=""
          srcLang="en"
          label="English"
        />
      </audio>
    );
  };

  const renderAudioSpeedDisplay = () => {
    return (
      <div
        className="text-xs hover:bg-foreground/10 ml-[4px] px-[12px] py-[6px] rounded-md cursor-pointer"
        onClick={(event) => {
          event.stopPropagation();
          onAudioSpeedClick();
        }}
        onKeyDown={onAudioSpeedClick}
      >
        {`x${audioData.audioSpeed}`}
      </div>
    );
  };

  const renderInnerSmall = () => (
    <div className="flex items-center">
      <div
        role="presentation"
        onClick={stopClickPropagation}
      >
        <Button
          color="primary"
          isIconOnly
          size="sm"
          className="w-auto h-auto data-[hover]:bg-foreground/10"
          radius="full"
          variant="light"
          onPress={onPlayPauseClick}
          isDisabled={!isAudioLoaded}
        >
          {audioData.isAudioPlaying ? (
            <AiFillPauseCircle size={28} />
          ) : (
            <AiFillPlayCircle size={28} />
          )}
        </Button>
      </div>
      {renderAudioSpeedDisplay()}
      <div
        role="presentation"
        onClick={stopClickPropagation}
        className="flex items-center ml-1 text-sm min-w-[65px] shrink-0 whitespace-nowrap"
      >
        <SeekableTime
          timeInSeconds={audioData.audioTime}
          durationInSeconds={audioData.audioDuration}
          onSeek={seekToSeconds}
          isDisabled={!isAudioLoaded}
          className="text-sm"
          inputClassName="w-[38px]"
        />
        <span>{`/${formattedAudioDuration}`}</span>
      </div>
      {renderAudio()}
      <div
        role="presentation"
        onClick={stopClickPropagation}
        className="flex max-w-md mx-2 grow"
      >
        <Slider
          aria-label="Audio progress"
          defaultValue={audioProgress}
          value={audioProgress}
          classNames={{
            base: "gap-3 grow",
            track: "border-s-secondary-100 min-w-[60px]",
            filler: "bg-gradient-to-r from-red-100 to-primary",
          }}
          renderThumb={(props) => (
            <div
              {...props}
              className="group p-0.5 top-1/2 bg-background border border-default-200 dark:border-default-400/50 shadow-medium rounded-full cursor-grab data-[dragging=true]:cursor-grabbing"
            >
              <span className="transition-transform bg-primary shadow-small rounded-full w-3 h-3 block group-data-[dragging=true]:scale-80" />
            </div>
          )}
          size="sm"
          onChange={onSliderChange}
          isDisabled={!isAudioLoaded}
        />
      </div>
    </div>
  );

  const renderInnerBig = () => {
    const isDisabled = !isAudioLoaded;
    return (
      <>
        <div className="flex flex-col mt-3 gap-0">
          {renderAudio()}
          {isAudioLoaded && (
            <div className="flex items-center justify-center mt-[4px]">
              <div className="grow">
                <Slider
                  aria-label="Music progress"
                  defaultValue={audioProgress}
                  value={audioProgress}
                  classNames={{
                    base: "max-w-md gap-3",
                    track: "border-s-secondary-100",
                    filler: "bg-gradient-to-r from-red-100 to-primary",
                  }}
                  renderThumb={(props) => (
                    <div
                      {...props}
                      className="group p-0.5 top-1/2 bg-background border border-default-200 dark:border-default-400/50 shadow-medium rounded-full cursor-grab data-[dragging=true]:cursor-grabbing"
                    >
                      <span className="transition-transform bg-primary shadow-small rounded-full w-3 h-3 block group-data-[dragging=true]:scale-80" />
                    </div>
                  )}
                  size="sm"
                  onChange={onSliderChange}
                  isDisabled={!isAudioLoaded}
                />
                <div className="flex justify-between">
                  <SeekableTime
                    timeInSeconds={audioData.audioTime}
                    durationInSeconds={audioData.audioDuration}
                    onSeek={seekToSeconds}
                    isDisabled={!isAudioLoaded}
                    className="text-[10px] tracking-widest"
                    inputClassName="w-[42px]"
                  />
                  <p className="text-[10px] text-foreground/50 tracking-widest">
                    {formattedAudioDuration}
                  </p>
                </div>
              </div>
              <div>{renderAudioSpeedDisplay()}</div>
            </div>
          )}
          {!isAudioLoaded && (
            <div className="w-full p-4 text-center text-sm text-red-500">
              {hasError
                ? "Audio file not available or error in playing audio."
                : "Audio file not available for this call."}
            </div>
          )}
        </div>
        <div className="flex w-full items-center justify-center">
          <div
            className="flex items-center justify-center cursor-pointer rounded-full w-[32px] h-[32px] hover:bg-foreground/10 mr-[4px]"
            onClick={onBackward}
            onKeyDown={onBackward}
          >
            <FiChevronsLeft size={14} />
          </div>
          <div
            className="audio-player__play-pause-btn flex items-center justify-center cursor-pointer rounded-full w-[40px] h-[40px] bg-[#EDEDED] hover:bg-foreground/10"
            onClick={onPlayPauseClick}
            onKeyDown={onPlayPauseClick}
          >
            {audioData.isAudioPlaying ? (
              <FaPause
                size={16}
                color="#525353"
              />
            ) : (
              <FaPlay
                className="pl-[4px]"
                size={16}
                color="#525353"
              />
            )}
          </div>
          <div
            className="flex items-center justify-center cursor-pointer rounded-full w-[32px] h-[32px] hover:bg-foreground/10 ml-[4px]"
            onClick={onForward}
            onKeyDown={onForward}
          >
            <FiChevronsRight size={14} />
          </div>
        </div>
      </>
    );
  };

  const cardWrapperClassName = `${
    isSidebar ? "z-50 rounded-md" : isCallCard ? "" : ""
  } ${className}`;
  const cardInnerCLassName = isSidebar ? "p-3" : isCallCard ? "px-6" : "";

  const playerCard = (
    <Card className={cardWrapperClassName}>
      <CardBody className={cardInnerCLassName}>
        {isSidebar && renderInnerSmall()}
        {isLiveContentSearch && renderInnerSmall()}
        {isCallCard && renderInnerBig()}
      </CardBody>
    </Card>
  );

  if (!canReturnToCall) return playerCard;

  // The click handler goes on a plain wrapper, not on Card: NextUI feeds Card's
  // onClick through useAriaButton with `isDisabled: !isPressable`, so it never
  // fires. Setting isPressable instead would hand react-aria the press
  // interaction and break dragging the progress slider inside.
  return (
    <div
      role="button"
      tabIndex={0}
      title={t("Revenir à l'appel en cours d'écoute")}
      onClick={onReturnToCall}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onReturnToCall();
        }
      }}
      className="cursor-pointer rounded-md transition-colors hover:brightness-95"
    >
      {playerCard}
    </div>
  );
};

export default MAudioPlayer;
